import { useRef, useState } from "react";
import { Bell, CheckCircle2, Loader2 } from "lucide-react";
import { api, formatApiError, track } from "@/lib/api";

const WaitlistFields = ({ p, onSubmit, sending, error }) => (
  <form onSubmit={onSubmit} className="waitlist-form" data-testid={`waitlist-form-${p.slug}`}>
    <div className="grid gap-3 sm:grid-cols-2">
      <div className="field mb-0">
        <label htmlFor={`${p.slug}-wl-email`} className="field-label">Email</label>
        <input id={`${p.slug}-wl-email`} name="email" type="email" required maxLength={200} autoComplete="email" className="nx-input" data-testid={`waitlist-input-email-${p.slug}`} />
      </div>
      <div className="field mb-0">
        <label htmlFor={`${p.slug}-wl-name`} className="field-label">Name <span className="normal-case tracking-normal text-nx-dim">(optional)</span></label>
        <input id={`${p.slug}-wl-name`} name="name" maxLength={120} autoComplete="name" className="nx-input" data-testid={`waitlist-input-name-${p.slug}`} />
      </div>
    </div>
    <div className="hp" aria-hidden="true">
      <label htmlFor={`${p.slug}-wl-website`}>Leave this field empty</label>
      <input id={`${p.slug}-wl-website`} name="website" tabIndex={-1} autoComplete="off" />
    </div>
    {error && <p role="alert" className="form-error mt-3" data-testid={`waitlist-error-${p.slug}`}>{error}</p>}
    <button type="submit" disabled={sending} className="btn btn-strong mt-4" data-testid={`waitlist-submit-${p.slug}`}>
      {sending && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />}
      {sending ? "Joining" : "Join waitlist"}
    </button>
  </form>
);

export const Waitlist = ({ p }) => {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const mountedAt = useRef(Date.now());

  const toggle = () => {
    if (!open) track("cta_click", { cta: `waitlist-${p.slug}` });
    setOpen(!open);
  };
  async function submit(e) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    setStatus("sending");
    setError("");
    try {
      await api.post("/waitlist", {
        product: p.slug, email: fd.get("email"), name: fd.get("name") || "", website: fd.get("website") || "",
        elapsed_ms: Date.now() - mountedAt.current,
      });
      track("form_submit", { cta: `waitlist-${p.slug}` });
      setStatus("done");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
      setStatus("idle");
    }
  }

  if (status === "done") {
    return (
      <p role="status" className="waitlist-done" data-testid={`waitlist-success-${p.slug}`}>
        <CheckCircle2 className="h-4 w-4 shrink-0 text-lime-300" aria-hidden="true" />
        You are on the list. We will email you when {p.name} launches.
      </p>
    );
  }
  return (
    <>
      <div className="module-foot">
        <span className="product-domain">Launching soon</span>
        <button type="button" className="btn btn-soon" aria-expanded={open} aria-controls={`waitlist-form-${p.slug}`} onClick={toggle} data-testid={`product-waitlist-${p.slug}`}>
          <Bell className="h-3.5 w-3.5" aria-hidden="true" /> Join the waitlist
        </button>
      </div>
      {open && <WaitlistFields p={p} onSubmit={submit} sending={status === "sending"} error={error} />}
    </>
  );
};
